// Created: 2026-04-11 by Douglas — Activity Feed slide-out panel (Phase 3)
// The Heartbeat: badge-scoped, date-grouped, dismissable activity feed
/* eslint-disable react-refresh/only-export-components */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  IconBell, IconCheck, IconX, IconCalendarCheck, IconWallet,
  IconCarCrash, IconTrophy, IconUsers, IconSettings, IconUserPlus,
  IconExternalLink, IconEye, IconSignature, IconMail, IconPhone,
  IconCalendarEvent, IconBrowser, IconChecklist, IconChevronRight,
} from '@tabler/icons-react';

// ─── Badge → Category mapping ────────────────────────────────────────────────
const BADGE_CATEGORY_MAP = {
  _base: ['assignment', 'financial', 'gamification', 'task'],
  'super-admin':          ['incident', 'lead', 'system', 'portal', 'email', 'team', 'agent_action'],
  'admin':                ['incident', 'lead', 'system', 'portal', 'email', 'team'],
  'admin-access':         ['incident', 'lead', 'system', 'portal', 'email', 'team'],
  'council':              ['incident', 'lead', 'system', 'portal', 'email', 'team'],
  'ops-claims':           ['incident'],
  'kot':                  ['team'],
  'events-lead':          ['lead'],
  'events-coordinator':   ['lead'],
  'bd-lead':              ['lead', 'portal'],
  'finance':              ['financial', 'portal'],
  'finance-collections':  ['financial', 'portal'],
  'finance-bookkeeping':  ['financial'],
  'client-portal-admin':  ['portal'],
};

function getVisibleCategories(userBadgeTypes) {
  const categories = new Set(BADGE_CATEGORY_MAP._base);
  for (const badge of userBadgeTypes) {
    const unlocked = BADGE_CATEGORY_MAP[badge] || [];
    unlocked.forEach(c => categories.add(c));
  }
  return Array.from(categories);
}

// ─── Category config ──────────────────────────────────────────────────────────
const CATEGORY_TABS = [
  { key: 'all',          label: 'All' },
  { key: 'assignment',   label: 'Shifts' },
  { key: 'financial',    label: 'Wallet' },
  { key: 'portal',       label: 'Portal' },
  { key: 'email',        label: 'Email' },
  { key: 'incident',     label: 'Incidents' },
  { key: 'gamification', label: 'Achievements' },
  { key: 'lead',         label: 'Leads' },
  { key: 'team',         label: 'Team' },
  { key: 'task',         label: 'Tasks' },
  { key: 'system',       label: 'System' },
];

const CATEGORY_STYLE = {
  assignment:   { icon: IconCalendarCheck, color: 'text-green-400',   bg: 'bg-green-500/20',    label: 'Shifts' },
  financial:    { icon: IconWallet,        color: 'text-[#AEA36D]',   bg: 'bg-[#AEA36D]/20',    label: 'Wallet' },
  incident:     { icon: IconCarCrash,      color: 'text-red-400',     bg: 'bg-red-500/20',       label: 'Incidents' },
  gamification: { icon: IconTrophy,        color: 'text-orange-400',  bg: 'bg-orange-500/20',    label: 'Achievements' },
  team:         { icon: IconUsers,         color: 'text-blue-400',    bg: 'bg-blue-500/20',      label: 'Team' },
  system:       { icon: IconSettings,      color: 'text-gray-400',    bg: 'bg-gray-500/20',      label: 'System' },
  lead:         { icon: IconUserPlus,      color: 'text-purple-400',  bg: 'bg-purple-500/20',    label: 'Leads' },
  portal:       { icon: IconBrowser,       color: 'text-cyan-400',    bg: 'bg-cyan-500/20',      label: 'Portal' },
  email:        { icon: IconMail,          color: 'text-indigo-400',  bg: 'bg-indigo-500/20',    label: 'Email' },
  task:         { icon: IconChecklist,     color: 'text-teal-400',    bg: 'bg-teal-500/20',      label: 'Tasks' },
  agent_action: { icon: IconSettings,      color: 'text-gray-400',    bg: 'bg-gray-500/20',      label: 'Agent' },
};

function getCategoryStyle(category) {
  return CATEGORY_STYLE[category] || CATEGORY_STYLE.system;
}

function urgencyBadge(urgency) {
  switch (urgency) {
    case 'urgent':    return 'bg-red-500/30 text-red-300 border border-red-500/40';
    case 'important': return 'bg-orange-500/30 text-orange-300 border border-orange-500/40';
    default:          return null;
  }
}

// ─── Date grouping ────────────────────────────────────────────────────────────
function getDateGroup(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  // Convert to ET for grouping
  const etFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
  const etDate = etFormatter.format(date);
  const etNow = etFormatter.format(now);

  if (etDate === etNow) return 'Today';

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const etYesterday = etFormatter.format(yesterday);
  if (etDate === etYesterday) return 'Yesterday';

  const weekAgo = new Date(now);
  weekAgo.setDate(weekAgo.getDate() - 7);
  if (date > weekAgo) return 'This Week';

  return 'Earlier';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTimeAgo(dateString) {
  if (!dateString) return '';
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1)  return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function inferCategory(notification) {
  if (notification.category) return notification.category;
  const t = notification.notification_type || '';
  if (t.includes('portal')) return 'portal';
  if (t.includes('email_curated')) return 'email';
  if (t.includes('incident') || t.includes('claim')) return 'incident';
  if (t.includes('contract') || t.includes('signature')) return 'system';
  if (t.includes('assignment') || t.includes('roster') || t.includes('approval')) return 'assignment';
  if (t.includes('payout') || t.includes('invoice') || t.includes('payment') || t.includes('finance')) return 'financial';
  if (t.includes('cashout') || t.includes('straggler') || t.includes('pay_dispute')) return 'financial';
  if (t.includes('coverage') || t.includes('check_in') || t.includes('check_out') || t.includes('documentation_required')) return 'assignment';
  if (t.includes('badge') || t.includes('xp') || t.includes('level') || t.includes('trophy')) return 'gamification';
  if (t.includes('lead') || t.includes('event_inquiry') || t.includes('job_application')) return 'lead';
  return 'system';
}

function isExternalTarget(target) {
  return /^(https?:|mailto:|tel:)/i.test(target || '');
}

function normalizeDeepLinkTarget(rawTarget, meta = {}) {
  if (!rawTarget) return null;
  const target = String(rawTarget).trim();
  if (!target) return null;
  if (isExternalTarget(target)) return target;

  const lower = target.toLowerCase();
  const path = lower
    .replace(/^gseatpro:\/\//, '')
    .replace(/^gseat:\/\//, '')
    .replace(/^\//, '');
  const recruitId = meta.application_id || meta.recruit_id || meta.candidate_id;
  const assignmentId = meta.assignment_id;
  const incidentId = meta.incident_id || meta.incident_report_id;
  const invoiceId = meta.invoice_id;
  const taskId = meta.task_id;
  const clientId = meta.client_id;
  const ticketId = meta.ticket_id;
  const pathParts = path.split(/[/?#]/).filter(Boolean);
  const pathId = pathParts[1];

  if (path.startsWith('recruit')) return recruitId || pathId ? `/admin/recruits?recruit=${recruitId || pathId}` : '/admin/recruits';
  if (path.startsWith('assignment')) return assignmentId || pathId ? `/assignments?assignment=${assignmentId || pathId}` : '/assignments';
  if (path.startsWith('incident') || path.startsWith('claim')) return incidentId || pathId ? `/incidents?incident=${incidentId || pathId}` : '/incidents';
  if (path.startsWith('wallet')) return '/finance';
  if (path.startsWith('finance')) return target.startsWith('/') ? target : `/${path}`;
  if (path.startsWith('payout')) return '/finance/payouts';
  if (path.startsWith('invoice')) return invoiceId || pathId ? `/finance/invoices?invoice=${invoiceId || pathId}` : '/finance/invoices';
  if (path.startsWith('task')) return taskId || pathId ? `/admin/tasks?task=${taskId || pathId}` : '/admin/tasks';
  if (path.startsWith('mail') || path.startsWith('email')) return meta.email_id ? `/mailroom?email=${meta.email_id}` : '/mailroom';
  if (path.startsWith('client')) return clientId || pathId ? `/clients?client=${clientId || pathId}` : '/clients';
  if (path.startsWith('ticket')) return ticketId || pathId ? `/assignments?ticket=${ticketId || pathId}` : '/assignments';
  if (path === 'madden-card' || path.startsWith('badge')) return '/valets/badges';
  if (target.startsWith('/')) return target;

  const screenMap = {
    RecruitingScreen: recruitId ? `/admin/recruits?recruit=${recruitId}` : '/admin/recruits',
    AssignmentDetail: assignmentId ? `/assignments?assignment=${assignmentId}` : '/assignments',
    Assignments: '/assignments',
    WalletHome: '/finance',
    KOTPayouts: '/finance/payouts',
    BadgeExplorer: '/valets/badges',
    IncidentsScreen: incidentId ? `/incidents?incident=${incidentId}` : '/incidents',
    Mailroom: meta.email_id ? `/mailroom?email=${meta.email_id}` : '/mailroom',
    MentorReview: recruitId ? `/admin/recruits?recruit=${recruitId}` : '/admin/recruits',
  };

  return screenMap[target] || null;
}

function resolveNavTarget(notification) {
  const meta = notification.metadata || notification.data || {};
  const explicitTarget = normalizeDeepLinkTarget(
    notification.deep_link || notification.action_url || meta.deep_link || meta.action_url || meta.targetScreen,
    meta,
  );
  if (explicitTarget) return explicitTarget;

  if (notification.notification_type === 'recruit_application' || meta.event_type === 'application_completed') {
    const recruitId = meta.application_id || meta.recruit_id || meta.candidate_id;
    return recruitId ? `/hr/recruits?recruit=${recruitId}` : '/hr/recruits';
  }

  if (meta.incident_id || meta.incident_report_id) return `/incidents?incident=${meta.incident_id || meta.incident_report_id}`;
  if (meta.client_id && notification.category === 'portal') return `/clients?client=${meta.client_id}`;
  if (meta.assignment_id) return `/assignments?assignment=${meta.assignment_id}`;
  if (meta.invoice_id)    return `/finance/invoices?invoice=${meta.invoice_id}`;
  if (meta.client_id)     return `/clients?client=${meta.client_id}`;
  if (meta.valet_id)      return `/admin/valets/${meta.valet_id}`;
  return null;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ActivityFeed({ open, onClose }) {
  const { valetProfile } = useAuth();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [visibleCategories, setVisibleCategories] = useState([...BADGE_CATEGORY_MAP._base]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const panelRef = useRef(null);

  // Load notifications — both read and unread, not dismissed
  const loadNotifications = useCallback(async () => {
    if (!valetProfile?.id) return;
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', valetProfile.id)
        .eq('dismissed', false)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setNotifications(data || []);
    } catch (err) {
      console.error('[ActivityFeed] Error loading:', err);
    }
  }, [valetProfile?.id]);

  // Init: load badges + notifications
  useEffect(() => {
    if (!valetProfile?.id) { setLoading(false); return; }

    const init = async () => {
      try {
        const { data: badges } = await supabase
          .from('badge_assignments')
          .select('badge_type')
          .eq('valet_id', valetProfile.id)
          .eq('is_active', true);

        const badgeTypes = (badges || []).map(b => b.badge_type);
        setVisibleCategories(getVisibleCategories(badgeTypes));
        await loadNotifications();
      } catch (err) {
        console.error('[ActivityFeed] Init error:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [valetProfile?.id, loadNotifications]);

  // Real-time subscription
  useEffect(() => {
    if (!valetProfile?.id) return;

    const channel = supabase
      .channel(`activity_feed_${valetProfile.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `recipient_id=eq.${valetProfile.id}`
      }, (payload) => {
        setNotifications(prev => [payload.new, ...prev].slice(0, 60));
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `recipient_id=eq.${valetProfile.id}`
      }, (payload) => {
        if (payload.new.dismissed) {
          setNotifications(prev => prev.filter(n => n.id !== payload.new.id));
        } else {
          setNotifications(prev => prev.map(n => n.id === payload.new.id ? payload.new : n));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [valetProfile?.id]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    // Delay to avoid catching the bell click
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClick), 100);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handleClick); };
  }, [open, onClose]);

  // ─── Actions ──────────────────────────────────────────────────────────────
  const dismissNotification = async (id) => {
    try {
      await supabase
        .from('notifications')
        .update({ dismissed: true, dismissed_at: new Date().toISOString(), read: true, read_at: new Date().toISOString() })
        .eq('id', id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('[ActivityFeed] Dismiss error:', err);
    }
  };

  const markNotificationRead = async (id) => {
    const readAt = new Date().toISOString();
    try {
      await supabase
        .from('notifications')
        .update({ read: true, read_at: readAt })
        .eq('id', id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true, read_at: readAt } : n));
    } catch (err) {
      console.error('[ActivityFeed] Mark read error:', err);
    }
  };

  const markAllDismissed = async () => {
    const visibleIds = filteredNotifications.map(n => n.id);
    if (visibleIds.length === 0) return;
    try {
      await supabase
        .from('notifications')
        .update({ dismissed: true, dismissed_at: new Date().toISOString(), read: true, read_at: new Date().toISOString() })
        .in('id', visibleIds);
      setNotifications(prev => prev.filter(n => !visibleIds.includes(n.id)));
    } catch (err) {
      console.error('[ActivityFeed] Dismiss all error:', err);
    }
  };

  const handleClick = (notification) => {
    const target = resolveNavTarget(notification);
    if (target) {
      if (isExternalTarget(target)) {
        window.open(target, '_blank', 'noopener,noreferrer');
      } else {
        navigate(target);
        onClose();
      }
    }
    // Mark read but don't dismiss — user navigated, they'll clear it later
    if (!notification.read) {
      markNotificationRead(notification.id);
    }
  };

  // ─── Filtering ────────────────────────────────────────────────────────────
  const filteredNotifications = activeTab === 'all'
    ? notifications.filter(n => visibleCategories.includes(inferCategory(n)))
    : notifications.filter(n => inferCategory(n) === activeTab);

  // Sort: urgent → important → normal → low, then by time
  const sorted = [...filteredNotifications].sort((a, b) => {
    const order = { urgent: 0, important: 1, normal: 2, low: 3 };
    const diff = (order[a.urgency] ?? 2) - (order[b.urgency] ?? 2);
    if (diff !== 0) return diff;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Group by date
  const grouped = [];
  let currentGroup = null;
  for (const n of sorted) {
    const group = getDateGroup(n.created_at);
    if (group !== currentGroup) {
      currentGroup = group;
      grouped.push({ type: 'header', label: group });
    }
    grouped.push({ type: 'item', notification: n });
  }

  const unreadCount = notifications.filter(n => !n.read && visibleCategories.includes(inferCategory(n))).length;

  // Visible tabs: only show tabs for categories the user can see
  const visibleTabs = CATEGORY_TABS.filter(tab =>
    tab.key === 'all' || visibleCategories.includes(tab.key)
  );

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="fixed top-0 right-0 h-full w-[440px] max-w-[90vw] bg-gray-950 border-l border-[#AEA36D]/30 shadow-2xl flex flex-col z-[99999]"
      style={{ animation: 'slideIn 0.2s ease-out' }}
    >
      {/* Header */}
      <div className="px-5 py-4 bg-black/60 border-b border-gray-800 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#AEA36D]/20 flex items-center justify-center">
              <IconBell className="w-4.5 h-4.5 text-[#AEA36D]" />
            </div>
            <div>
              <span className="text-[#AEA36D] font-bold text-sm tracking-wider">ACTIVITY FEED</span>
              {unreadCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#AEA36D] text-black">
                  {unreadCount} new
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {filteredNotifications.length > 0 && (
              <button
                onClick={markAllDismissed}
                className="text-xs text-gray-500 hover:text-[#AEA36D] transition-colors px-2 py-1 rounded hover:bg-gray-800"
              >
                Clear all
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-white transition-colors p-1 rounded hover:bg-gray-800"
            >
              <IconX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Category tabs — scrollable */}
        <div className="flex gap-1 overflow-x-auto pb-0.5 -mx-1 px-1 scrollbar-none">
          {visibleTabs.map(tab => {
            const count = tab.key === 'all'
              ? filteredNotifications.length
              : notifications.filter(n => inferCategory(n) === tab.key).length;
            const isActive = activeTab === tab.key;
            const style = tab.key !== 'all' ? getCategoryStyle(tab.key) : null;

            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all border ${
                  isActive
                    ? 'bg-[#AEA36D]/20 text-[#AEA36D] border-[#AEA36D]/50'
                    : 'bg-gray-900/50 text-gray-500 border-gray-800 hover:border-gray-700 hover:text-gray-400'
                }`}
              >
                {style && <style.icon className={`w-3.5 h-3.5 ${isActive ? style.color : 'text-gray-600'}`} />}
                {tab.label}
                {count > 0 && (
                  <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    isActive ? 'bg-[#AEA36D] text-black' : 'bg-gray-800 text-gray-400'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Feed items */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#AEA36D] mx-auto" />
          </div>
        ) : grouped.length === 0 ? (
          <EmptyState category={activeTab} />
        ) : (
          <div className="p-3">
            {grouped.map((entry, i) => {
              if (entry.type === 'header') {
                return (
                  <div key={`h-${entry.label}`} className={`flex items-center gap-3 ${i > 0 ? 'mt-4' : ''} mb-2 px-1`}>
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">{entry.label}</span>
                    <span className="flex-1 h-px bg-gray-800" />
                  </div>
                );
              }
              return (
                <FeedItem
                  key={entry.notification.id}
                  notification={entry.notification}
                  onClick={() => handleClick(entry.notification)}
                  onMarkRead={() => markNotificationRead(entry.notification.id)}
                  onDismiss={() => dismissNotification(entry.notification.id)}
                />
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0.8; }
          to { transform: translateX(0); opacity: 1; }
        }
        .scrollbar-none::-webkit-scrollbar { display: none; }
        .scrollbar-none { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

// ─── Feed Item ────────────────────────────────────────────────────────────────
function FeedItem({ notification, onClick, onMarkRead, onDismiss }) {
  const category = inferCategory(notification);
  const style = getCategoryStyle(category);
  const CategoryIcon = style.icon;
  const badge = urgencyBadge(notification.urgency);
  const isUnread = !notification.read;
  const navTarget = resolveNavTarget(notification);
  const canOpen = Boolean(navTarget);

  const isContract = notification.notification_type?.includes('contract');
  const DisplayIcon = isContract
    ? (notification.notification_type === 'contract_signed' ? IconSignature : IconEye)
    : CategoryIcon;
  const iconColor = isContract
    ? (notification.notification_type === 'contract_signed' ? 'text-green-400' : 'text-purple-400')
    : style.color;

  return (
    <div
      className={`p-3 rounded-lg border cursor-pointer transition-all group mb-1.5 ${
        isUnread ? 'border-l-2' : ''
      } ${
        category === 'incident'
          ? `border-red-500/30 bg-red-900/10 hover:border-red-400/50 ${isUnread ? 'border-l-red-400' : ''}`
          : category === 'financial'
            ? `border-[#AEA36D]/20 bg-[#AEA36D]/5 hover:border-[#AEA36D]/40 ${isUnread ? 'border-l-[#AEA36D]' : ''}`
            : category === 'portal'
              ? `border-cyan-500/20 bg-cyan-900/5 hover:border-cyan-400/40 ${isUnread ? 'border-l-cyan-400' : ''}`
              : `border-gray-800 bg-gray-900/40 hover:border-gray-700 ${isUnread ? 'border-l-gray-400' : ''}`
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`shrink-0 w-7 h-7 rounded-lg ${style.bg} flex items-center justify-center`}>
            <DisplayIcon className={`w-4 h-4 ${iconColor}`} />
          </div>
          <div className="min-w-0">
            <span className={`text-sm font-medium line-clamp-1 ${isUnread ? 'text-white' : 'text-gray-300'}`}>
              {notification.title}
            </span>
            {notification.message && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{notification.message}</p>
            )}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          className="shrink-0 text-gray-700 hover:text-gray-400 transition-colors opacity-0 group-hover:opacity-100 p-0.5"
          title="Dismiss"
        >
          <IconX className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Lead context */}
      {category === 'lead' && notification.metadata && <LeadContext metadata={notification.metadata} />}

      {/* Footer */}
      <div className="mt-2 pl-9">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-600">{formatTimeAgo(notification.created_at)}</span>
          <div className="flex items-center gap-1.5">
            {badge && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${badge}`}>
                {notification.urgency}
              </span>
            )}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${style.bg} ${style.color}`}>
              {style.label}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mt-2">
          {canOpen && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClick(); }}
              className="inline-flex items-center gap-1 h-7 px-2 rounded border border-gray-700 bg-gray-950/60 text-[11px] font-semibold text-gray-300 hover:text-white hover:border-gray-600 transition-colors"
              title={isExternalTarget(navTarget) ? 'Open external link' : 'Open'}
            >
              {isExternalTarget(navTarget) ? <IconExternalLink className="w-3 h-3" /> : <IconChevronRight className="w-3 h-3" />}
              Open
            </button>
          )}
          {isUnread && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
              className="inline-flex items-center gap-1 h-7 px-2 rounded border border-gray-700 bg-gray-950/60 text-[11px] font-semibold text-gray-300 hover:text-white hover:border-gray-600 transition-colors"
              title="Mark read"
            >
              <IconCheck className="w-3 h-3" />
              Read
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            className="inline-flex items-center gap-1 h-7 px-2 rounded border border-gray-800 bg-gray-950/40 text-[11px] font-semibold text-gray-500 hover:text-gray-300 hover:border-gray-700 transition-colors"
            title="Dismiss"
          >
            <IconX className="w-3 h-3" />
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadContext({ metadata }) {
  const items = [];
  if (metadata.email) items.push({ icon: IconMail, label: metadata.email });
  if (metadata.phone) items.push({ icon: IconPhone, label: metadata.phone });
  if (metadata.event_type && metadata.venue_name) items.push({ icon: IconCalendarEvent, label: `${metadata.event_type} at ${metadata.venue_name}` });
  if (items.length === 0) return null;
  return (
    <div className="mt-1.5 pl-9 space-y-0.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
          <item.icon className="w-3 h-3 shrink-0" />
          <span className="truncate">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ category }) {
  const messages = {
    all:          { icon: IconBell,          text: 'All clear',              sub: 'No activity to show' },
    assignment:   { icon: IconCalendarCheck, text: 'No shift updates',       sub: 'Assignment activity appears here' },
    financial:    { icon: IconWallet,        text: 'Wallet is quiet',        sub: 'Payment and invoice activity appears here' },
    incident:     { icon: IconCarCrash,      text: 'No incident alerts',     sub: 'Claims and incidents appear here' },
    gamification: { icon: IconTrophy,        text: 'No new achievements',    sub: 'Badges and XP milestones appear here' },
    lead:         { icon: IconUserPlus,      text: 'No new leads',           sub: 'Event inquiries appear here' },
    system:       { icon: IconSettings,      text: 'No system alerts',       sub: 'System notifications appear here' },
    portal:       { icon: IconBrowser,       text: 'No portal activity',     sub: 'Client logins, invoice views, and payments appear here' },
    email:        { icon: IconMail,          text: 'No email alerts',        sub: 'Curated email notifications appear here' },
    task:         { icon: IconChecklist,     text: 'No pending tasks',       sub: 'Task assignments appear here' },
    team:         { icon: IconUsers,         text: 'No team updates',        sub: 'Team notifications appear here' },
  };
  const { icon: Icon, text, sub } = messages[category] || messages.all;
  return (
    <div className="p-12 text-center">
      <div className="w-14 h-14 rounded-full bg-gray-900 flex items-center justify-center mx-auto mb-4">
        <Icon className="w-7 h-7 text-gray-600" />
      </div>
      <p className="text-sm font-medium text-gray-400">{text}</p>
      <p className="text-xs text-gray-600 mt-1">{sub}</p>
    </div>
  );
}

// Export the unread count hook for the bell badge
export function useUnreadCount() {
  const { valetProfile } = useAuth();
  const [count, setCount] = useState(0);
  const [hasUrgent, setHasUrgent] = useState(false);

  useEffect(() => {
    if (!valetProfile?.id) return;

    // Exact head counts, not a row fetch. The old query pulled up to 100 rows on every
    // realtime event and capped the number it showed, so a recipient with 20,547 unread read
    // the same "9+" as one with 10. Two cheap count-only calls; coalesced so a burst of
    // inserts costs one refresh.
    let timer = null;
    const loadNow = async () => {
      const base = () => supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', valetProfile.id)
        .eq('read', false)
        .eq('dismissed', false);
      const [all, urgent] = await Promise.all([base(), base().eq('urgency', 'urgent')]);
      if (all.error) return; // keep the last good number; a decoration must not lie or break
      setCount(all.count || 0);
      setHasUrgent((urgent.count || 0) > 0);
    };
    const load = () => { clearTimeout(timer); timer = setTimeout(loadNow, 800); };
    loadNow();

    const channel = supabase
      .channel(`unread_count_${valetProfile.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `recipient_id=eq.${valetProfile.id}`
      }, () => { load(); })
      .subscribe();

    return () => { clearTimeout(timer); supabase.removeChannel(channel); };
  }, [valetProfile?.id]);

  return { count, hasUrgent };
}
